// Metrics tracking utility for user interactions
class MetricsTracker {
  constructor() {
    this.metrics = [];
    this.sessionId = this.generateSessionId();
    this.startTime = new Date();
    this.lastTrackedEvents = {}; // Prevent duplicate events
  }

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Track user interactions (simple - just track clicks)
  trackEvent(eventType, data = {}) {
    const event = {
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      eventType: eventType,
      ...data
    };
    
    this.metrics.push(event);
    
    // Automatically save to localStorage
    this.saveMetricsToStorage();
  }

  // Track level selection (simple)
  trackLevelSelected(operation, level) {
    this.trackEvent('level_selected', {
      operation: operation,
      level: level,
      questionCount: 1 // Practice mode has 1 question at a time
    });
  }

  // Track check answer action (simple)
  trackCheckAnswer(operation, level, isCorrect) {
    this.trackEvent('check_answer', {
      operation: operation,
      level: level,
      questionCount: 1, // Practice mode has 1 question at a time
      isCorrect: isCorrect
    });
  }

  // Track show solution action (simple)
  trackShowSolution(operation, level) {
    this.trackEvent('show_solution', {
      operation: operation,
      level: level,
      questionCount: 1 // Practice mode has 1 question at a time
    });
  }

  // Track exam events
  trackExamStarted(operation, level, questionCount) {
    this.trackEvent('exam_started', {
      operation: operation,
      level: level,
      questionCount: questionCount
    });
  }

  trackExamCompleted(operation, level, score, totalQuestions, timeSpent) {
    this.trackEvent('exam_completed', {
      operation: operation,
      level: level,
      score: score,
      totalQuestions: totalQuestions,
      timeSpent: timeSpent
    });
  }

  trackExamSubmitted(operation, level, score, totalQuestions, correctAnswers) {
    this.trackEvent('exam_submitted', {
      operation: operation,
      level: level,
      score: score,
      totalQuestions: totalQuestions,
      correctAnswers: correctAnswers
    });
  }

  // Track print events
  trackPrintWorkbook(operation, level, questionCount) {
    this.trackEvent('print_workbook', {
      operation: operation,
      level: level,
      questionCount: questionCount
    });
  }

  trackPrintQuestions(operation, level, questionCount) {
    this.trackEvent('print_questions', {
      operation: operation,
      level: level,
      questionCount: questionCount
    });
  }

  trackPrintAnswers(operation, level, questionCount) {
    this.trackEvent('print_answers', {
      operation: operation,
      level: level,
      questionCount: questionCount
    });
  }


  // Convert metrics to CSV format (simplified)
  convertToCSV() {
    if (this.metrics.length === 0) {
      return 'No metrics data available';
    }

    const headers = [
      'timestamp',
      'eventType',
      'operation',
      'level',
      'questionCount',
      'isCorrect'
    ];

    const csvRows = [headers.join(',')];

    this.metrics.forEach(event => {
      const row = headers.map(header => {
        const value = event[header] !== undefined ? event[header] : '';
        // Escape commas and quotes in CSV
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  }

  // Download metrics as CSV file (Admin only)
  downloadMetrics() {
    const csvContent = this.convertToCSV();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `math_practice_metrics_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Save metrics to localStorage for persistence across sessions
  saveMetricsToStorage() {
    try {
      // Only save the current session metrics, don't append to existing
      localStorage.setItem('mathPracticeMetrics', JSON.stringify(this.metrics));
    } catch (error) {
      console.error('Error saving metrics to localStorage:', error);
    }
  }

  // Load metrics from localStorage
  loadMetricsFromStorage() {
    try {
      const storedMetrics = JSON.parse(localStorage.getItem('mathPracticeMetrics') || '[]');
      this.metrics = storedMetrics;
      return storedMetrics;
    } catch (error) {
      console.error('Error loading metrics from localStorage:', error);
      return [];
    }
  }

  // Download all stored metrics (Admin function - simplified)
  downloadAllMetrics() {
    const allMetrics = this.loadMetricsFromStorage();
    if (allMetrics.length === 0) {
      alert('No metrics data available');
      return;
    }

    const headers = [
      'timestamp',
      'eventType',
      'operation',
      'level',
      'questionCount',
      'isCorrect'
    ];

    const csvRows = [headers.join(',')];

    allMetrics.forEach(event => {
      const row = headers.map(header => {
        const value = event[header] !== undefined ? event[header] : '';
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `math_practice_metrics_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Get metrics summary
  getMetricsSummary() {
    const summary = {
      totalEvents: this.metrics.length,
      sessionDuration: new Date() - this.startTime,
      operationsUsed: [...new Set(this.metrics.map(m => m.operation).filter(Boolean))],
      levelsUsed: [...new Set(this.metrics.map(m => m.level).filter(Boolean))],
      checkAnswers: this.metrics.filter(m => m.eventType === 'check_answer').length,
      correctAnswers: this.metrics.filter(m => m.eventType === 'check_answer' && m.isCorrect).length,
      examsCompleted: this.metrics.filter(m => m.eventType === 'exam_completed').length,
      printEvents: this.metrics.filter(m => m.eventType.includes('print')).length
    };

    return summary;
  }

  // Clear metrics (for testing or reset)
  clearMetrics() {
    this.metrics = [];
    this.sessionId = this.generateSessionId();
    this.startTime = new Date();
  }
}

// Create a singleton instance
const metricsTracker = new MetricsTracker();

export default metricsTracker;
